import type { AdminAlertSeverity } from "@/types/admin";
import { parseUnassignedPayload } from "@/features/routes/lib/unassignedBreakdown";
import {
  buildResultOutcome,
  parseBuildResultNotification,
} from "@/features/routes/lib/buildResultToast";

/**
 * The ONE place the platform decides whether a notification reports a success, a
 * warning or a failure.
 *
 * Why this module exists: `NotificationBell` (TASK-4741) already derived
 * `isFailure` inline, and `useNotificationsWebSocket` needed the SAME judgement
 * to pick between `toastSuccess` / `toastWarning` / `toastError`. Two independent
 * copies of that predicate is how a row ends up flagged as a failure in the bell
 * while the toast for the very same event pops green — so the predicate lives
 * here and both surfaces call it.
 *
 * ---------------------------------------------------------------------------
 * What it can key on TODAY
 * ---------------------------------------------------------------------------
 * Two independent signals carry failure evidence, and both are live:
 *
 *   1. an unassigned breakdown in `extra_data` (TASK-4740) — a row that names
 *      dropped orders is by definition a failure report; and
 *   2. `severity`, which the backend half of TASK-4743 now populates on
 *      failure rows via `resolve_severity` (`critical` / `warning`).
 *
 * Both reach the browser over BOTH transports: the REST bell payload and the
 * WS `notification.new` frame. The WS frame used to drop `severity` and
 * `extra_data` entirely, which made every socket-delivered failure structurally
 * indistinguishable from a success — the backend half fixed that in
 * `service._push_to_websocket` + `consumers.notification_new`, and the producer
 * resolves severity through the same `resolve_severity` the bell uses, so the
 * two transports cannot disagree.
 *
 * Both signals are already load-bearing in the bell, so this function is
 * deliberately the same predicate and not a second, subtly different one.
 *
 * The platform severity scale is `info | warning | critical` — there is no
 * `high`, so nothing maps from one.
 */

/** How a notification should be presented to the dispatcher. */
export type NotificationOutcome = "success" | "warning" | "error";

/**
 * Minimal shape needed to judge an outcome. Satisfied by both the REST
 * `Notification` row and the WS `notification.new` message, which is the point:
 * the bell and the toast judge the same event identically.
 */
export interface OutcomeBearingNotification {
  notification_type?: string | null;
  severity?: AdminAlertSeverity | null;
  extra_data?: Record<string, unknown> | null;
}

/**
 * The outcome of a CODED `routes_built` row, or `null` for any other row.
 *
 * BUG-677 (leftover of BUG-676): a build that created routes and dropped some
 * orders carries an unassigned breakdown, and the breakdown alone used to make
 * the bell badge it «Ошибка» while the toast for the same build was already a
 * warning. A coded row states its route count, so it is judged by the SAME rule
 * the build toast uses (`buildResultOutcome`): nothing created → error, some
 * orders dropped → warning, everything placed → success. A row written before
 * TASK-4743 has no code and no route count; it keeps the breakdown predicate
 * below, because nothing on it says whether any route was created.
 */
function codedBuildOutcome(
  notification: OutcomeBearingNotification,
): NotificationOutcome | null {
  if (notification.notification_type !== "routes_built") return null;
  const result = parseBuildResultNotification(notification.extra_data);
  return result ? buildResultOutcome(result) : null;
}

/**
 * True when the notification reports that something FAILED.
 *
 * Extracted verbatim from `NotificationBell`'s TASK-4741 predicate — presence of
 * an unassigned breakdown, or a `critical` severity — except for a coded
 * `routes_built` row, which is judged by its outcome (see `codedBuildOutcome`).
 * Do not add a second failure predicate elsewhere; extend this one.
 */
export function isFailureNotification(
  notification: OutcomeBearingNotification,
): boolean {
  const build = codedBuildOutcome(notification);
  if (build !== null) return build === "error";
  return (
    parseUnassignedPayload(notification.extra_data) !== null ||
    notification.severity === "critical"
  );
}

/**
 * Routes a notification to its toast flavour and its bell badge.
 *
 * `critical` and a dropped-order breakdown are failures; `warning` severity is a
 * warning; everything else stays a success. A coded `routes_built` row follows
 * the build-toast rule instead (partial → warning). A failure must never render
 * as a green success toast — that is the defect this function removes
 * (`useNotificationsWebSocket` called `toastSuccess` unconditionally, so
 * «Не удалось построить маршруты» popped green).
 */
export function notificationOutcome(
  notification: OutcomeBearingNotification,
): NotificationOutcome {
  const build = codedBuildOutcome(notification);
  if (build !== null) return build;
  if (isFailureNotification(notification)) return "error";
  if (notification.severity === "warning") return "warning";
  return "success";
}
